import { VaPreviewPage } from './components/AutoVaCard'
import { useEffect, useState, useRef, lazy, Suspense } from 'react'
import { useApp } from './context/AppContext'
import { useAuth } from './context/AuthContext'
import NavBar from './components/NavBar'
import WaitlistPage from './pages/WaitlistPage'
import LoginPage from './pages/LoginPage'
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const WelcomeShowcase = lazy(() => import('./pages/WelcomeShowcase'))
const StudioPage = lazy(() => import('./pages/StudioPage'))
const FormatPage = lazy(() => import('./pages/FormatPage'))
const PublicAccountAnalysis = lazy(() => import('./pages/PublicAccountAnalysis'))
const PublicVideoAnalysis = lazy(() => import('./pages/PublicVideoAnalysis'))
const LandingPage = lazy(() => import('./pages/LandingPage'))
import { getLegalPageForCurrentPath } from './pages/LegalPages'
const StrategyPage = lazy(() => import('./pages/StrategyPage'))
const EvaluatePage = lazy(() => import('./pages/EvaluatePage'))
const ExplorePage = lazy(() => import('./pages/ExplorePage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const ClientAnalysisTab = lazy(() => import('./components/admin/ClientAnalysisTab'))
const LibraryPage = lazy(() => import('./pages/LibraryPage'))
import MaintenancePage from './pages/MaintenancePage'
const MerlinChat = lazy(() => import('./components/MerlinChat'))
import { LowCreditsBanner } from './components/Gating'
import BetaBanner from './components/BetaBanner'
import FeedbackFAB from './components/FeedbackFAB'
import { API_URL } from './utils/config'
import { trackPurchase } from './utils/metaPixel'

// Splash de chargement des chunks (code-splitting 2026-07-07) : sobre,
// instantané, cohérent avec les deux thèmes.
function ChunkSplash() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 34, height: 34, borderRadius: '50%', border: '3px solid rgba(124,58,237,.22)', borderTopColor: '#7c3aed', animation: 'chunkspin .8s linear infinite' }} />
      <style>{'@keyframes chunkspin{to{transform:rotate(360deg)}}'}</style>
    </div>
  )
}

// Banner stack under NavBar. Renders inline (takes layout space) so it never
// clashes visually with the page content. If no banners are active, only a minimal
// nav clearance stays. If one or both are active, they stack with real breathing room.
function FloatingBannerStack() {
  const { isSimulating, isSimulatingUsage, maxCredits, usagePct, isUnlimited } = useAuth()
  const [dismissed] = useState(() => {
    try { return sessionStorage.getItem('cg_low_credits_dismissed') === 'true' } catch { return false }
  })
  const pct = typeof usagePct === 'function' ? usagePct() : 0
  const lowCreditsVisible = !isUnlimited && maxCredits !== -1 && !dismissed && pct >= 90
  const hasPlan = isSimulating || isSimulatingUsage
  const hasAny = hasPlan || lowCreditsVisible
  return (
    <div className={`flex flex-col items-center gap-2.5 px-3 ${hasAny ? 'pt-14 pb-3' : 'pt-12'}`}>
      <PlanSimulationBanner inline />
      <LowCreditsBanner inline />
    </div>
  )
}

// Banner shown when a plan / credit simulation is active (admin testing)
// Rendered in normal flow (inline=true) via BannerStack so it takes layout space.
function PlanSimulationBanner({ inline = false }) {
  const { isSimulating, plan, realPlan, setSimulatedPlan, isSimulatingUsage, simulatedUsagePct, setSimulatedUsagePct } = useAuth()
  if (!isSimulating && !isSimulatingUsage) return null

  const outerClass = inline
    ? 'pointer-events-auto animate-in max-w-full'
    : 'fixed top-[52px] left-1/2 -translate-x-1/2 z-[940] animate-in'

  return (
    <div className={outerClass}>
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full shadow-lg text-xs font-semibold max-w-[92vw]"
        style={{ background: 'linear-gradient(135deg, #f59e0b, #fe8204)', color: 'white', boxShadow: '0 4px 20px rgba(245,158,11,0.3)' }}>
        <span aria-hidden>🎭</span>
        {isSimulating && (
          <>
            <span>Plan <strong className="uppercase">{plan}</strong></span>
            <span className="opacity-70 text-xs">(réel: {realPlan})</span>
          </>
        )}
        {isSimulatingUsage && (
          <>
            {isSimulating && <span className="opacity-60 mx-0.5">·</span>}
            <span>Crédits <strong>{simulatedUsagePct}%</strong></span>
          </>
        )}
        {isSimulating && (
          <button onClick={() => setSimulatedPlan(null)}
            className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold transition-all hover:scale-105"
            style={{ background: 'rgba(255,255,255,0.22)' }}
            title="Arrêter la simulation de plan">
            ✕ Plan
          </button>
        )}
        {isSimulatingUsage && (
          <button onClick={() => setSimulatedUsagePct(null)}
            className="px-2 py-0.5 rounded-full text-xs font-bold transition-all hover:scale-105"
            style={{ background: 'rgba(255,255,255,0.22)' }}
            title="Arrêter la simulation de crédits">
            ✕ Crédits
          </button>
        )}
      </div>
    </div>
  )
}

function App() {
  try {
    const _q = new URLSearchParams(window.location.search)
    if (_q.get('vapreview') === '1' && window.location.hostname === 'localhost') return <VaPreviewPage />
  } catch { /* no-op */ }

  const { page, setPage, profile, profiles, profilesRestoring, profilesRestoreDone } = useApp()
  const { user, loading, needsPlan, needsApproval, needsRejectionNotice, signOut, trackEvent, isAdmin } = useAuth()
  const [enteringApp, setEnteringApp] = useState(false)
  const prevPageRef = useRef(page)

  // ── Maintenance mode ────────────────────────────────────────
  // Poll le backend (/api/maintenance) toutes les 30s. Si actif,
  // affiche MaintenancePage a tous SAUF admin (qui doit pouvoir debugger).
  // Poll en continu car l'admin peut flip pendant qu'un user est connecte :
  // on veut que ses fenetres basculent en maintenance dans les 30s.
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' })
  useEffect(() => {
    let alive = true
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/maintenance`, { cache: 'no-store' })
        if (!res.ok) return
        const data = await res.json()
        if (alive) setMaintenance({ enabled: !!data.enabled, message: data.message || '' })
      } catch { /* backend down = on laisse passer, pas la peine de bloquer */ }
    }
    check()
    const id = setInterval(check, 30000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  // Post-onboarding : l'user atterrit sur 'Ma stratégie' (library) pour
  // analyser son compte Instagram + voir son plan. La page 'Mes scripts'
  // (strategy) reste accessible via navbar pour le feed de formats.
  useEffect(() => {
    if (profiles.length > 0 && page === 'onboarding' && profile) {
      setPage('library')
    }
  }, [])

  // Analytics — track every page navigation (from → to) for the beta funnel
  useEffect(() => {
    if (!user || !page) return
    const from = prevPageRef.current
    if (from === page) return
    trackEvent('page_view', {
      from: from || null,
      to: page,
      has_profile: !!profile,
    })
    prevPageRef.current = page
  }, [page, user?.id])

  // Handle Stripe payment return
  const [paymentToast, setPaymentToast] = useState(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const payment = params.get('payment')
    if (payment === 'success') {
      const plan = params.get('plan') || ''
      const billing = params.get('billing') || 'monthly'
      // Studio = essai 7j → Purchase avec valeur réduite (proxy de conversion
      // trial→paid). Le vrai prélèvement J+15 envoie la valeur pleine côté
      // serveur (webhook invoice.paid). Permet d'optimiser des ad campaigns
      // Meta sur l'objectif "Purchase" sans gonfler le ROAS apparent.
      const isStudioTrial = plan.startsWith('studio_')
      trackPurchase({ plan, billingPeriod: billing, isTrialStart: isStudioTrial })
      setPaymentToast(`Paiement reussi ! Bienvenue en ${plan.toUpperCase()}.`)
      window.history.replaceState({}, '', window.location.pathname)
      // Refresh user data to pick up new plan
      setTimeout(() => window.location.reload(), 2500)
    } else if (payment === 'cancelled') {
      setPaymentToast('Paiement annule.')
      window.history.replaceState({}, '', window.location.pathname)
      setTimeout(() => setPaymentToast(null), 4000)
    }
  }, [])

  // Dark-to-light transition when leaving onboarding
  useEffect(() => {
    const wasOnboarding = prevPageRef.current === 'onboarding'
    prevPageRef.current = page
    if (wasOnboarding && page !== 'onboarding') {
      setEnteringApp(true)
      const timer = setTimeout(() => setEnteringApp(false), 1800)
      return () => clearTimeout(timer)
    }
  }, [page])

  // Auto-flag showcase comme vu pour tout user existant (sécurité contre
  // re-trigger accidentel du WelcomeShowcase lors d'un hard refresh).
  // ★ Doit rester avec les autres hooks au top du composant (Rules of Hooks).
  useEffect(() => {
    if (profiles.length > 0 || profile) {
      try { localStorage.setItem('cg_showcase_seen', 'true') } catch {}
    }
  }, [profiles.length, profile])

  // Auth loading
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange/30 border-t-orange rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-ink-muted">Chargement...</p>
        </div>
      </div>
    )
  }

  // Maintenance mode — bloque tout le monde sauf admin. Placé apres le
  // loading pour eviter un flash, et avant tout le reste pour que meme
  // les users non connectes (et les users en attente beta) voient l'ecran.
  //
  // Triple safety net pour l'admin bypass, resistant aux races :
  //   1. isAdmin standard (via useAuth user.email)
  //   2. URL query param ?admin-force=1 (bypass d'urgence si race condition)
  //   3. Fallback lecture directe du token Supabase en localStorage
  //
  // Le cas (1) peut rater si user n'est pas encore propagé dans le context
  // au moment du render (race entre supabase session restore + fetch maintenance).
  // Les cases (2) et (3) garantissent qu'on ne se verrouille jamais dehors.
  const urlBypass = typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).has('admin-force') ||
     new URLSearchParams(window.location.search).has('bypass-maintenance'))

  const adminEmailInStorage = (() => {
    if (typeof window === 'undefined') return false
    try {
      // Supabase stocke la session sous une clé sb-<projectref>-auth-token
      const keys = Object.keys(localStorage).filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
      for (const k of keys) {
        const raw = localStorage.getItem(k)
        if (!raw) continue
        const v = JSON.parse(raw)
        const email = v?.user?.email || v?.currentSession?.user?.email
        if (email === 'antonin.girardeau.pro@gmail.com') return true
      }
    } catch {}
    return false
  })()

  const isAdminBypass = isAdmin || urlBypass || adminEmailInStorage

  if (maintenance.enabled && !isAdminBypass) {
    return <MaintenancePage message={maintenance.message} />
  }

  // Not authenticated → Login
  // DEV BYPASS: ?dev=1 skips auth and injects mock profile — REMOVE BEFORE PUSH
  const devBypass = import.meta.env.DEV && new URLSearchParams(window.location.search).has('dev')
  if (devBypass && !user) {
    // Inject mock profile if none exists, so we can navigate to strategy/other pages
    const devPage = new URLSearchParams(window.location.search).get('page')
    if (devPage && devPage !== page) {
      setPage(devPage)
    }
    if (profiles.length === 0 && !profile) {
      // Auto-inject a demo profile to access all pages
      const mockProfile = {
        id: 'p_dev_mock',
        name: 'Coach Fitness (Dev)',
        description: 'Coach fitness en ligne specialise dans la perte de poids pour les debutants. Methode basee sur 15 min par jour sans materiel.',
        audience: 'Hommes et femmes 25-45 ans sedentaires qui veulent perdre du poids',
        audienceTarget: 'Debutants fitness sedentaires',
        audiencePain: 'Ils ont essaye 10 regimes et abandonne a chaque fois apres 2 semaines',
        audienceDesire: 'Se sentir bien dans leur corps et avoir l energie de jouer avec leurs enfants',
        audienceEnemy: 'L industrie du fitness qui vend des programmes trop compliques et chers',
        creatorMethod: 'Methode 15 min/jour sans materiel, resultats en 30 jours',
        emotionalTone: 'authentique',
        productionStyle: 'face_cam',
        postsPerWeek: 5,
        objective: 'grow',
        hasProduct: true,
      }
      // Use localStorage directly to inject profile
      try {
        localStorage.setItem('cg_profiles', JSON.stringify([mockProfile]))
        localStorage.setItem('cg_active_idx', '0')
        window.location.reload()
        return null
      } catch {}
    }
  }
  if (!user && !devBypass) {
    return <LoginPage />
  }

  // Beta gate step 0: explicit rejection — separate screen so the user knows
  // it's not "pending" (which suggests "maybe soon"). Wins over everything.
  if (needsRejectionNotice && !devBypass) {
    return <WaitlistPage step="rejected" onSignOut={signOut} />
  }

  // Beta gate step 1: waiting for manual validation (pending)
  if (needsApproval && !devBypass) {
    return <WaitlistPage step="pending" onSignOut={signOut} />
  }

  // Beta gate step 2: approved but needs a paid plan (admin didn't grant one)
  if (needsPlan && !devBypass) {
    return <WaitlistPage step="plan" onSignOut={signOut} />
  }

  // Authenticated → App
  // Guard contre le flash OnboardingPage en navigation privée : si l'user est
  // authentifié mais qu'on n'a pas encore tenté/fini de restaurer ses profils
  // depuis Supabase, on affiche un loader. Sans ça, l'user voit un onboarding
  // démarrer alors qu'il a déjà un profil DB → croit qu'il doit recommencer.
  const userMaybeHasDbProfile = !profile && profiles.length === 0 && (profilesRestoring || !profilesRestoreDone)
  if (userMaybeHasDbProfile && page !== 'onboarding') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-purple-950 via-purple-900 to-indigo-950 text-white">
        <div className="w-12 h-12 border-2 border-orange/40 border-t-orange rounded-full animate-spin mb-4" />
        <p className="text-sm text-white/70">Récupération de ton profil…</p>
      </div>
    )
  }
  // Force preview du onboarding via URL param ?test_onboarding=1 (bouton admin
  // dans NavBar). Bypass complet du gate "profile existant".
  let testOnboarding = false
  try { testOnboarding = new URLSearchParams(window.location.search).get('test_onboarding') === '1' } catch {}
  if (testOnboarding) {
    return <Suspense fallback={<ChunkSplash />}><WelcomeShowcase /></Suspense>
  }

  // (Studio MVP routing déplacé dans AppWithStudioPreview wrapper plus bas.
  // Le wrapper intercepte ?studio=1 et ?studio=preview AVANT App, pour que
  // StudioPage puisse gérer son propre flow signup sans gate auth d'App.)

  if (page === 'onboarding' || (!profile && page !== 'admin' && page !== 'explore' && page !== 'client' && page !== 'library')) {
    // Nouveau flow showcase : UNIQUEMENT pour les vrais brand-new users.
    // Condition stricte : zéro profile DB (pas juste profile actif null) ET
    // pas encore vu le showcase. Sinon → OnboardingPage classique (qui sait
    // déjà gérer la création d'un nouveau profile additionnel).
    let seenShowcase = false
    try { seenShowcase = localStorage.getItem('cg_showcase_seen') === 'true' } catch {}
    const isBrandNewUser = profiles.length === 0 && !profile
    if (isBrandNewUser && !seenShowcase && page !== 'onboarding') {
      return <Suspense fallback={<ChunkSplash />}><WelcomeShowcase /></Suspense>
    }
    return <Suspense fallback={<ChunkSplash />}><OnboardingPage /></Suspense>
  }

  return (
    <>
    {/* NavBar + BetaBanner rendus HORS .merlin-app — sinon le transform de
        l'animation contentEmerge casse le positioning fixed (un fixed enfant
        d'un element avec transform devient relative a cet element, plus a
        la viewport → d'ou le gap invisible qu'on chassait) */}
    <NavBar />
    <BetaBanner />
    <div className={`merlin-app${enteringApp ? ' merlin-app-entering' : ''}`}>
      {enteringApp && <div className="merlin-veil" />}
      {/* Floating banners stack — always below NavBar, proper gap between each */}
      <FloatingBannerStack />
      <Suspense fallback={<ChunkSplash />}>
        {page === 'strategy' && <StrategyPage />}
        {page === 'evaluate' && <EvaluatePage />}
        {page === 'explore' && <ExplorePage />}
        {page === 'admin' && <AdminPage />}
        {page === 'client' && <div className="max-w-6xl mx-auto px-6 py-6"><ClientAnalysisTab /></div>}
        {page === 'library' && <LibraryPage />}
      </Suspense>
    </div>
    <Suspense fallback={null}><MerlinChat /></Suspense>
    <FeedbackFAB />

    {/* Payment toast */}
    {paymentToast && (
      <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[1010] animate-in">
        <div className="px-6 py-3 rounded-2xl shadow-2xl text-[12px] font-bold text-white"
          style={{ background: paymentToast.includes('annule') ? 'linear-gradient(135deg, #6b7280, #4b5563)' : 'linear-gradient(135deg, #22c55e, #16a34a)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
          {paymentToast.includes('annule') ? '✕ ' : '✓ '}{paymentToast}
        </div>
      </div>
    )}
    </>
  )
}

// Wrapper qui shortcut le rendu vers StudioPage en mode preview (?studio=preview),
// AVANT que App ne déclare ses hooks/contexts. Évite tout problème "rendered fewer hooks"
// car l'URL est stable au mount et le wrapper rend soit le mock soit l'App complète.
// Détection runtime du domaine Studio. Permet d'avoir 1 seul déploiement
// Railway servant LES DEUX produits selon le hostname visité.
// merlin-content-ai.com → Merlin classique
// studio.merlin-content-ai.com → Studio uniquement
// Override possible via VITE_APP_MODE=studio (utile pour tests build locale).
function isStudioHostname() {
  try {
    if (import.meta.env.VITE_APP_MODE === 'studio') return true
    const h = window.location.hostname.toLowerCase()
    if (h.startsWith('studio.') || h === 'studio.merlin-content-ai.com') return true
    // Dev/test : ?studio=1 (ou =preview) force le mode Studio sur n'importe quel
    // host et on le persiste pour survivre à la navigation SPA / aux refresh.
    // Sortie : ?studio=0 nettoie le flag.
    const sp = new URLSearchParams(window.location.search).get('studio')
    if (sp === '0') { try { localStorage.removeItem('cg_studio_mode') } catch {}; return false }
    if (sp === '1' || sp === 'preview') { try { localStorage.setItem('cg_studio_mode', '1') } catch {}; return true }
    if (localStorage.getItem('cg_studio_mode') === '1') return true
  } catch { /* fallback Merlin */ }
  return false
}

export default function AppWithStudioPreview() {
  // ── RETOUR DE CHECKOUT STRIPE CÔTÉ STUDIO ──
  // Le handler historique vit dans App() (Merlin classique), qui ne monte
  // JAMAIS sur studio.* : aucun Purchase pixel n'est parti côté Studio
  // depuis le lancement (constat 2026-07-07, "aucun achat dans le pixel").
  // Hook placé AVANT les early-returns (règle des hooks).
  useEffect(() => {
    if (!isStudioHostname()) return
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.get('payment') === 'success') {
        const plan = params.get('plan') || ''
        const billing = params.get('billing') || 'monthly'
        trackPurchase({ plan, billingPeriod: billing, isTrialStart: plan.startsWith('studio_') })
        params.delete('payment'); params.delete('plan'); params.delete('billing')
        const qs = params.toString()
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
      }
    } catch { /* no-op */ }
  }, [])

  // ── PAGE FORMAT PUBLIQUE — /format/:id ──
  // Cible des vignettes des articles SEO. Rendue AVANT tout routing/auth, sur
  // les deux domaines, pour que le visiteur voie le format SUR Merlin (reel
  // embarqué + storyboard) sans gate. Le gating (beats 2+) est interne à la page.
  try {
    const fm = window.location.pathname.match(/^\/format\/([0-9a-fA-F-]{8,})\/?$/)
    if (fm) return <Suspense fallback={<ChunkSplash />}><FormatPage id={fm[1]} /></Suspense>
  } catch { /* fallback routing normal */ }

  // ── PAGE ANALYSE COMPTE PUBLIQUE — /compte/:id ──
  // Vitrine + partage prospect : l'analyse « Mon compte » d'un user, en lecture
  // seule, sans compte requis. Rendue AVANT tout routing/auth, comme /format/:id.
  // L'id est un UUID non devinable. CTA collant vers Studio en bas de page.
  try {
    const cm = window.location.pathname.match(/^\/compte\/([0-9a-fA-F-]{8,})\/?$/)
    if (cm) return <Suspense fallback={<ChunkSplash />}><PublicAccountAnalysis id={cm[1]} /></Suspense>
  } catch { /* fallback routing normal */ }

  // ── PAGE ANALYSE VIDEO PUBLIQUE — /video/:id ──
  // Vitrine « voir l'analyse » du mail (modèle daily format /format/:id) :
  // l'analyse d'une vidéo en lecture seule, sans compte requis.
  try {
    const vm = window.location.pathname.match(/^\/video\/([0-9a-fA-F-]{8,})\/?$/)
    if (vm) return <Suspense fallback={<ChunkSplash />}><PublicVideoAnalysis id={vm[1]} /></Suspense>
  } catch { /* fallback routing normal */ }

  // ── MODE STUDIO STANDALONE (par hostname uniquement) ──
  // Studio est un produit complètement séparé de Merlin :
  //   merlin-content-ai.com         → Merlin classique
  //   studio.merlin-content-ai.com  → Studio uniquement
  // Aucun passage possible d'un produit à l'autre depuis l'UI.
  // Abonnements distincts (users.plan vs users.studio_plan), localStorage
  // session séparé (domaine différent → Supabase auth isolée).
  // Override VITE_APP_MODE=studio gardé pour le build local de dev Studio.
  // Test LP : ?lp=1 → rendre la nouvelle landing page (Studio uniquement,
  // pour validation visuelle avant promotion en route racine).
  if (isStudioHostname()) {
    // Pages légales (CGU, CGV, mentions, confidentialité) — accessibles
    // sans auth, avant tout autre routing Studio.
    const legalPage = getLegalPageForCurrentPath()
    if (legalPage) return legalPage
    // Route /admin : admin panel disponible aussi sur studio.* pour gérer
    // les users Studio (statut Stripe, funnel events, etc.) sans devoir
    // switcher de domaine. La gate admin email reste dans AdminPage.
    try {
      const path = window.location.pathname.toLowerCase().replace(/\/$/, '')
      if (path === '/admin') {
        return (
          <div className="min-h-screen bg-bg">
            <Suspense fallback={<ChunkSplash />}><AdminPage /></Suspense>
          </div>
        )
      }
    } catch { /* fallback Studio */ }
    try {
      const params = new URLSearchParams(window.location.search)
      // ?lp=1, ?lp=2 ... — accepte n'importe quelle valeur de lp pour bypass
      // le cache navigateur quand on push une nouvelle version de la LP.
      if (params.get('lp')) return <Suspense fallback={<ChunkSplash />}><LandingPage /></Suspense>
    } catch { /* fallback Studio */ }
    return <Suspense fallback={<ChunkSplash />}><StudioPage /></Suspense>
  }
  // Sur le domaine Merlin : aussi exposer les pages légales (footer Merlin
  // y pointe également via les liens partagés).
  {
    const legalPage = getLegalPageForCurrentPath()
    if (legalPage) return legalPage
  }
  return <App />
}
